/**
 * KPICards component displaying key portfolio metrics.
 *
 * Displays metrics from the server-computed account summary KPI data:
 * - Realized P&L: actual gains/losses from closed positions (FIFO-based, 30d window)
 * - Win Rate: ratio of profitable closed trades to total closed trades (30d)
 * - Open Positions: count of currently active positions (updated in 30d)
 * - Avg Position Size: average cost basis of active positions (30d)
 *
 * Metrics requiring net liquidating value (portfolio return %, daily P&L, drawdown)
 * are intentionally excluded until NLV data is available from the broker API.
 */

import { Card, CardContent, AnimatedNumber } from '@/components/ui';
import { formatCurrency, formatPercent } from '@/utils/formatters';
import type { AccountKPI } from '@/types';

interface KPICardsProps {
  kpi?: AccountKPI | null;
  isLoading?: boolean;
}

export interface KPIMetrics {
  /** Sum of realized_pnl from positions closed in the last 30 days */
  realizedPnl: number;
  /** Number of closed positions with positive realized P&L / total closed (last 30d) */
  winRate: number;
  /** Count of currently active positions (updated in last 30d) */
  openPositionCount: number;
  /** Average cost basis of active positions */
  avgPositionSize: number;
  /** Number of closed positions in the window (denominator for win rate) */
  closedCount: number;
  /** Number of profitable closed positions in the window */
  winsCount: number;
  /** Mean realized P&L of winning trades (30d window). 0 if no winners. */
  avgWin: number;
  /** Mean realized P&L of losing trades (30d window, always <= 0). 0 if no losers. */
  avgLoss: number;
  /** Count of losing trades in 30d window */
  lossesCount: number;
}

/**
 * Convert server KPI data to the internal KPIMetrics format.
 */
export function calculateKPIMetrics(kpi?: AccountKPI | null): KPIMetrics {
  if (!kpi) {
    return {
      realizedPnl: 0,
      winRate: 0,
      openPositionCount: 0,
      avgPositionSize: 0,
      closedCount: 0,
      winsCount: 0,
      avgWin: 0,
      avgLoss: 0,
      lossesCount: 0,
    };
  }

  return {
    realizedPnl: kpi.realized_pnl,
    winRate: kpi.win_rate,
    openPositionCount: kpi.open_position_count,
    avgPositionSize: kpi.avg_position_size,
    closedCount: kpi.closed_count,
    winsCount: kpi.wins_count,
    avgWin: kpi.avg_win ?? 0,
    avgLoss: kpi.avg_loss ?? 0,
    lossesCount: kpi.losses_count ?? 0,
  };
}

function getValueSizeClass(formattedLength: number): string {
  if (formattedLength >= 10) return 'text-base';
  if (formattedLength >= 8) return 'text-lg';
  return 'text-2xl';
}

function KPICard({
  title,
  value,
  subtitle,
  trend,
  icon,
  formatter,
}: {
  title: string;
  value: number;
  subtitle?: string;
  trend?: 'up' | 'down' | 'neutral';
  icon?: React.ReactNode;
  formatter?: (v: number) => string;
}) {
  const trendColors = {
    up: 'text-emerald-400',
    down: 'text-rose-400',
    neutral: 'text-dark-300',
  };

  const formattedValue = formatter ? formatter(value) : value.toLocaleString();
  const sizeClass = getValueSizeClass(formattedValue.length);

  return (
    <Card hover={false} className="group">
      <CardContent className="py-5">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1 min-w-0 flex-1">
            <p className="text-sm font-medium text-dark-400">{title}</p>
            <div className="h-8 flex items-center">
              <AnimatedNumber
                value={value}
                formatter={formatter}
                className={`${sizeClass} font-bold font-mono tabular-nums ${trend ? trendColors[trend] : 'text-dark-100'}`}
              />
            </div>
          </div>
          {icon && (
            <div className="rounded-lg bg-dark-700/50 p-2 text-dark-400 group-hover:text-primary-400 transition-colors flex-shrink-0">
              {icon}
            </div>
          )}
        </div>
        {subtitle && (
          <p className={`text-xs mt-2 ${trend ? trendColors[trend] : 'text-dark-500'}`}>
            {subtitle}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export function KPICards({ kpi, isLoading = false }: KPICardsProps) {
  const metrics = calculateKPIMetrics(kpi);

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Card key={i}>
            <CardContent className="py-5">
              <div className="space-y-3">
                <div className="h-4 w-24 rounded shimmer" />
                <div className="h-8 w-32 rounded shimmer" />
                <div className="h-3 w-20 rounded shimmer" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const pnlTrend = metrics.realizedPnl > 0 ? 'up' : metrics.realizedPnl < 0 ? 'down' : 'neutral';
  const avgWinTrend = metrics.avgWin > 0 ? 'up' : 'neutral';
  const avgLossTrend = metrics.avgLoss < 0 ? 'down' : 'neutral';

  const avgWinSubtitle = metrics.winsCount > 0
    ? `From ${metrics.winsCount} winning trade${metrics.winsCount !== 1 ? 's' : ''}`
    : 'No winning trades';

  const avgLossSubtitle = metrics.lossesCount > 0
    ? `From ${metrics.lossesCount} losing trade${metrics.lossesCount !== 1 ? 's' : ''}`
    : 'No losing trades in window';

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      <KPICard
        title="Realized P&L (30d)"
        value={metrics.realizedPnl}
        formatter={(v) => formatCurrency(v)}
        subtitle={`From ${metrics.closedCount} closed position${metrics.closedCount !== 1 ? 's' : ''}`}
        trend={pnlTrend}
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
      />
      <KPICard
        title="Win Rate (30d)"
        value={metrics.winRate}
        formatter={(v) => formatPercent(v)}
        subtitle={metrics.closedCount > 0
          ? `${metrics.winsCount} of ${metrics.closedCount} closed trades`
          : 'No closed trades in window'}
        trend={metrics.closedCount > 0
          ? (metrics.winRate >= 50 ? 'up' : 'down')
          : 'neutral'}
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
      />
      <KPICard
        title="Avg Win (30d)"
        value={metrics.avgWin}
        formatter={(v) => formatCurrency(v)}
        subtitle={avgWinSubtitle}
        trend={avgWinTrend}
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        }
      />
      <KPICard
        title="Avg Loss (30d)"
        value={metrics.avgLoss}
        formatter={(v) => formatCurrency(v)}
        subtitle={avgLossSubtitle}
        trend={avgLossTrend}
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
          </svg>
        }
      />
      <KPICard
        title="Open Positions"
        value={metrics.openPositionCount}
        formatter={(v) => v.toFixed(0)}
        subtitle="Active in last 30 days"
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        }
      />
      <KPICard
        title="Avg Position Size"
        value={metrics.avgPositionSize}
        subtitle="Active positions"
        formatter={(v) => formatCurrency(v)}
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
          </svg>
        }
      />
    </div>
  );
}
